import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  BusinessCenter as OwnerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Home as PropertyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import StatCard from '../../components/data/StatCard';
import { profilesApi, propertiesApi } from '../../../lib/api';
import { useApi } from '../../../hooks/useApi';

interface Owner {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  phone_number?: string;
  status: 'active' | 'inactive' | 'pending';
  nationality?: string;
  is_foreign?: boolean;
  created_at: string;
  updated_at: string;
}

const statusColors = {
  active: 'success',
  inactive: 'default',
  pending: 'warning',
} as const;

export default function OwnersList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { t } = useTranslation(['owners', 'common', 'people']);

  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch owners from Supabase
  const {
    data: owners,
    loading,
    error,
    refetch
  } = useApi(() => profilesApi.getOwners(), []);

  // Fetch properties to count per owner
  const { data: properties } = useApi(() => propertiesApi.getAll(), []);

  // Calculate properties per owner
  const ownerPropertyCounts: Record<string, number> = {};
  (properties || []).forEach((prop: any) => {
    if (prop.owner_id) {
      ownerPropertyCounts[prop.owner_id] = (ownerPropertyCounts[prop.owner_id] || 0) + 1;
    }
  });

  // Show error message if API call fails
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error]);

  // Filter owners
  const filteredOwners = (owners || []).filter((owner: Owner) => {
    const searchLower = searchTerm.toLowerCase();
    const displayPhone = owner.phone_number || owner.phone || '';
    return (
      owner.first_name?.toLowerCase().includes(searchLower) ||
      owner.last_name?.toLowerCase().includes(searchLower) ||
      owner.email?.toLowerCase().includes(searchLower) ||
      displayPhone.toLowerCase().includes(searchLower)
    );
  });

  // Calculate stats
  const stats = {
    total: owners?.length || 0,
    active: owners?.filter((o: Owner) => o.status === 'active').length || 0,
    inactive: owners?.filter((o: Owner) => o.status === 'inactive').length || 0,
    totalProperties: properties?.length || 0,
  };

  return (
    <ResponsiveContainer>
      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2rem' },
            }}
          >
            {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('subtitle')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refetch}
            disabled={loading}
            sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
          >
            {t('common:refresh')}
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/dashboard/owners/add')}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          >
            {t('addOwner')}
          </Button>
        </Box>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            title={t('totalOwners')}
            value={stats.total}
            icon={<OwnerIcon />}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title={t('activeOwners')}
            value={stats.active}
            icon={<OwnerIcon />}
            color="success"
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title={t('inactiveOwners')}
            value={stats.inactive}
            icon={<OwnerIcon />}
            color="warning"
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title={t('totalProperties')}
            value={stats.totalProperties}
            icon={<PropertyIcon />}
            color="info"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={t('searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Results Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('found')}: {filteredOwners.length}
      </Typography>

      {/* Owners Grid */}
      {loading ? (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={56} height={56} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="text" width="40%" height={20} />
                    </Box>
                  </Box>
                  <Skeleton variant="text" width="80%" height={20} />
                  <Skeleton variant="text" width="70%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filteredOwners.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
          }}
        >
          <OwnerIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('noOwnersFound')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? t('adjustSearch') : t('addFirstOwner')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {filteredOwners.map((owner: Owner) => {
            const displayPhone = owner.phone_number || owner.phone;
            const propertyCount = ownerPropertyCounts[owner.id] || 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={owner.id}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[4],
                    },
                  }}
                  onClick={() => navigate(`/dashboard/owners/${owner.id}`)}
                >
                  <CardContent>
                    {/* Header with Avatar and Status */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: 'primary.main',
                          fontSize: '1.25rem',
                        }}
                      >
                        {owner.first_name?.[0] || ''}
                        {owner.last_name?.[0] || ''}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          {owner.first_name} {owner.last_name}
                        </Typography>
                        <Chip
                          label={t(`statusOptions.${owner.status}`)}
                          color={statusColors[owner.status] || 'default'}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>

                    {/* Contact Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {owner.email}
                      </Typography>
                    </Box>

                    {displayPhone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {displayPhone}
                        </Typography>
                      </Box>
                    )}

                    {/* Properties Count */}
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <PropertyIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2" color="text.secondary">
                        {propertyCount} {t('propertiesOwned')}
                      </Typography>
                    </Box>

                    {owner.is_foreign && (
                      <Chip
                        label={t('isForeign')}
                        size="small"
                        color="info"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </ResponsiveContainer>
  );
}
